import { format, parseISO } from 'date-fns';
import { dailyNavAsOfLabel } from 'common/constants/dailyNav';
import {
  DailyNavMarketDataReadiness,
  DailyNavReportSourceRow,
  DailyNavValidationCompany,
  FirmDailyNavDay,
} from 'pages/Intelligence/DailyNav/daily-nav.api.types';
import {
  CHECK_COPY,
  MARKET_DATA_PROVIDER_LABELS,
  MARKET_DATA_RECHECK_BUFFER_MS,
  MARKET_DATA_RECHECK_FALLBACK_MS,
  MARKET_DATA_RECHECK_MIN_MS,
} from './ReportDialog.constants';


export const formatWhen = (value?: string | null) => {
  if (!value) {
    return '';
  }
  try {
    return format(parseISO(value), 'MMM d, yyyy, h:mm a');
  } catch {
    return value;
  }
};

export const formatSftpDelivery = (day?: FirmDailyNavDay | null, isSent = false) => {
  const host = day?.ftp_host;
  if (isSent) {
    if (day?.ftp_delivered) {
      return host ? `SFTP delivered to ${host}` : 'SFTP delivered';
    }
    if (day?.ftp_configured) {
      return 'SFTP not delivered';
    }
    return 'SFTP not delivered (not configured)';
  }
  if (day?.ftp_configured) {
    return host ? `SFTP will deliver to ${host}` : 'SFTP configured';
  }
  return 'SFTP not configured';
};

export const formatEmailDelivery = (
  day?: FirmDailyNavDay | null,
  recipients: string[] = [],
  isSent = false
) => {
  if (isSent) {
    if (day?.email_delivered) {
      return recipients.length ? `Email delivered to ${recipients.join(', ')}` : 'Email delivered';
    }
    if (recipients.length) {
      return 'Email not delivered';
    }
    return 'Email not delivered (no recipients)';
  }
  return recipients.length
    ? `Email will send to ${recipients.join(', ')}`
    : 'Email not configured';
};

export const formatPrice = (value?: string | null, currency?: string | null) => {
  if (!value) {
    return '—';
  }
  const amount = Number(value);
  if (Number.isNaN(amount)) {
    return value;
  }
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatDate = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch {
    return value;
  }
};

export const formatFailedCompany = (company: DailyNavValidationCompany, rows: DailyNavReportSourceRow[]) => {
  const row = rows.find(reportRow => reportRow.company_id === company.company_id);
  if (company.cause === 'carry_forward_mismatch' && row?.valuation_price_per_share) {
    return `${company.company_name} (today ${formatPrice(row.valuation_price_per_share, row.currency)})`;
  }
  // The inverse complaint: a new price DID land, on a day the reviewer set to
  // hold its opening one. Says so outright, or it reads as the mismatch above.
  if (company.cause === 'deferred_price_moved' && row?.valuation_price_per_share) {
    const movedTo = formatPrice(row.valuation_price_per_share, row.currency);
    return `${company.company_name} (price deferred to next day, but today's mark moved to ${movedTo})`;
  }
  if (company.cause === 'deferred_price_moved') {
    return `${company.company_name} (price deferred to next day, but today's mark moved)`;
  }
  if (company.cause === 'triggered_incomplete') {
    return `${company.company_name} (review incomplete)`;
  }
  if (company.cause === 'started_incomplete') {
    return `${company.company_name} (in progress)`;
  }
  if (company.cause === 'price_not_delivered') {
    return `${company.company_name} (new price not posted)`;
  }
  if (company.cause === 'missing_mark') {
    return `${company.company_name} (no mark)`;
  }
  return company.company_name;
};

// Names the instant checked: mid-session that is "now", later the cutoff.
export const marketDataCheckLabel = (marketData?: DailyNavMarketDataReadiness | null) => {
  const asOf = dailyNavAsOfLabel(marketData?.snapshot_at);
  return asOf
    ? `Secondary and public comp data has finished loading as of ${asOf}`
    : CHECK_COPY.market_data_complete;
};

const formatTimeOfDay = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const parsed = parseISO(value);
  // Zone named, like the check's own "as of" label beside it.
  return Number.isNaN(parsed.getTime())
    ? ''
    : new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(parsed);
};

export const isPendingJob = (status: DailyNavMarketDataReadiness['jobs'][number]['status']) =>
  status === 'NOT_STARTED' || status === 'IN_PROGRESS';

export const providersWhere = (
  marketData: DailyNavMarketDataReadiness,
  matches: (status: DailyNavMarketDataReadiness['jobs'][number]['status']) => boolean
) =>
  Array.from(
    new Set(
      marketData.jobs
        .filter(job => matches(job.status))
        .map(job => MARKET_DATA_PROVIDER_LABELS[job.provider_code] ?? job.provider_code)
    )
  ).join(', ');

// Why the check failed, and what happens next. Empty when it passed.
export const marketDataFailureDetail = (marketData?: DailyNavMarketDataReadiness | null) => {
  if (!marketData) {
    return '';
  }
  if (marketData.status === 'pending') {
    const providers = providersWhere(marketData, isPendingJob);
    const expected = formatTimeOfDay(marketData.expected_by);
    return `Still loading${providers ? `: ${providers}` : ''}${
      expected ? ` (expected by ${expected})` : ''
    }. Checking again automatically.`;
  }
  if (marketData.status === 'stalled') {
    const providers = providersWhere(marketData, isPendingJob);
    return `${providers || 'A data provider'} has been loading for over 15 minutes. This day can be finalized, but those prices may still change afterwards.`;
  }
  if (marketData.status === 'failed') {
    const providers = providersWhere(marketData, status => status === 'FAILED');
    return `${providers || 'A data provider'} did not finish loading, so some prices may be from earlier runs. This day can still be finalized.`;
  }
  if (marketData.status === 'unavailable') {
    return 'Could not confirm whether the market data has finished loading. Re-run validations to try again.';
  }
  return '';
};

// When to ask again while data is in flight; null when there is nothing to wait for.
export const marketDataRecheckDelayMs = (
  marketData?: DailyNavMarketDataReadiness | null,
  nowMs: number = Date.now()
): number | null => {
  // Stalled already lets finalize pass, so stop; refresh or window focus still re-asks.
  if (marketData?.status !== 'pending') {
    return null;
  }
  // The ETA and the stall deadline still ahead of us: either can let the check pass.
  const upcoming = [marketData.expected_by, marketData.stalls_at]
    .map(value => (value ? Date.parse(value) : Number.NaN))
    .filter(ms => !Number.isNaN(ms) && ms > nowMs);
  if (!upcoming.length) {
    return MARKET_DATA_RECHECK_FALLBACK_MS;
  }
  // Ask a buffer after the sooner one, so the run has landed by then; never sooner than the minimum.
  const untilSoonestMs = Math.min(...upcoming) - nowMs;
  return Math.max(untilSoonestMs + MARKET_DATA_RECHECK_BUFFER_MS, MARKET_DATA_RECHECK_MIN_MS);
};
